import React from "react";
import { Check } from "lucide-react";

interface Step {
  name: string;
  description: string;
  status: "complete" | "current" | "upcoming";
}

interface OnboardingProgressProps {
  currentStep: number;
  completedSteps: Set<number>;
  isMobile?: boolean;
  onStepClick?: (stepIndex: number) => void;
  canSkipLegal?: boolean;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export default function OnboardingProgress({
  currentStep,
  completedSteps,
  isMobile = false,
  onStepClick,
  canSkipLegal = false,
}: OnboardingProgressProps) {
  const steps: Step[] = [
    {
      name: "Welcome",
      description: "Get started with Health Harbor",
      status: "upcoming",
    },
    {
      name: "Legal",
      description: canSkipLegal ? "Skipped - Pre-authorized" : "Terms and privacy information",
      status: "upcoming",
    },
    {
      name: "Practice & Provider",
      description: "Your practice and provider details",
      status: "upcoming",
    },
    {
      name: "Call Scripts",
      description: "Customize your AI call scripts",
      status: "upcoming",
    },
    {
      name: "Payment",
      description: "Complete your account setup",
      status: "upcoming",
    },
  ];

  // Update step statuses based on current step and completed steps
  const updatedSteps = steps.map((step, index) => {
    // If legal step (index 1) can be skipped and is not the current step, mark as complete
    if (index === 1 && canSkipLegal && index !== currentStep) {
      return { ...step, status: "complete" as const };
    } else if (completedSteps.has(index)) {
      return { ...step, status: "complete" as const };
    } else if (index === currentStep) {
      return { ...step, status: "current" as const };
    } else {
      return { ...step, status: "upcoming" as const };
    }
  });

  // Mobile horizontal stepper
  if (isMobile) {
    return (
      <nav aria-label="Progress" className="w-full">
        <div className="flex items-center justify-between">
          {updatedSteps.map((step, stepIdx) => (
            <div
              key={step.name}
              className="flex flex-col items-center flex-1 min-w-0"
            >
              {/* Step indicator */}
              <div className="flex items-center w-full">
                {/* Connection line (before) */}
                {stepIdx > 0 && (
                  <div
                    className={classNames(
                      "flex-1 h-0.5 mx-1",
                      step.status === "complete" || stepIdx < currentStep
                        ? "bg-[#005151]"
                        : "bg-gray-300"
                    )}
                  />
                )}

                {/* Step circle */}
                <div
                  className={classNames(
                    "relative flex-shrink-0",
                    (step.status === "complete" || step.status === "current") &&
                      onStepClick
                      ? "cursor-pointer hover:scale-105 transition-transform"
                      : ""
                  )}
                  onClick={() => {
                    if (
                      (step.status === "complete" ||
                        step.status === "current") &&
                      onStepClick
                    ) {
                      onStepClick(stepIdx);
                    }
                  }}
                >
                  {step.status === "complete" ? (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#005151] shadow-sm hover:bg-[#004040] transition-colors">
                      <Check className="h-3.5 w-3.5 text-white" />
                    </div>
                  ) : step.status === "current" ? (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#005151] bg-white shadow-sm">
                      <div className="h-2.5 w-2.5 rounded-full bg-[#005151]" />
                    </div>
                  ) : (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                      <div className="h-2 w-2 rounded-full bg-gray-300" />
                    </div>
                  )}
                </div>

                {/* Connection line (after) */}
                {stepIdx < updatedSteps.length - 1 && (
                  <div
                    className={classNames(
                      "flex-1 h-0.5 mx-1",
                      completedSteps.has(stepIdx)
                        ? "bg-[#005151]"
                        : "bg-gray-300"
                    )}
                  />
                )}
              </div>

              {/* Step label */}
              <div className="mt-2 text-center px-1">
                <div
                  className={classNames(
                    "text-xs font-medium leading-tight",
                    step.status === "current"
                      ? "text-[#005151]"
                      : step.status === "complete"
                        ? "text-gray-700"
                        : "text-gray-400"
                  )}
                >
                  {/* Truncate long names on mobile */}
                  {step.name.length > 10 ? step.name.split(" ")[0] : step.name}
                </div>
              </div>
            </div>
          ))}
        </div>
      </nav>
    );
  }

  // Desktop vertical stepper
  return (
    <nav aria-label="Progress">
      <ol className="overflow-hidden">
        {updatedSteps.map((step, stepIdx) => (
          <li
            key={step.name}
            className={classNames(
              stepIdx !== updatedSteps.length - 1 ? "pb-8" : "",
              "relative"
            )}
          >
            {step.status === "complete" ? (
              <>
                {stepIdx !== updatedSteps.length - 1 ? (
                  <div
                    aria-hidden="true"
                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-[#005151]"
                  />
                ) : null}
                <div
                  className={classNames(
                    "group relative flex items-start",
                    onStepClick
                      ? "cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                      : ""
                  )}
                  onClick={() => onStepClick && onStepClick(stepIdx)}
                >
                  <span className="flex h-9 items-center">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#005151] group-hover:bg-[#004040] transition-colors shadow-md">
                      <Check
                        aria-hidden="true"
                        className="h-4 w-4 text-white"
                      />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    <span className="text-sm font-semibold text-gray-900 group-hover:text-[#005151] transition-colors">
                      {step.name}
                    </span>
                    <span className="text-xs text-gray-600 mt-0.5">
                      {step.description}
                    </span>
                  </span>
                </div>
              </>
            ) : step.status === "current" ? (
              <>
                {stepIdx !== updatedSteps.length - 1 ? (
                  <div
                    aria-hidden="true"
                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                  />
                ) : null}
                <div
                  aria-current="step"
                  className={classNames(
                    "group relative flex items-start",
                    onStepClick
                      ? "cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                      : ""
                  )}
                  onClick={() => onStepClick && onStepClick(stepIdx)}
                >
                  <span aria-hidden="true" className="flex h-9 items-center">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#005151] bg-white shadow-md">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#005151]" />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    <span className="text-sm font-semibold text-[#005151]">
                      {step.name}
                    </span>
                    <span className="text-xs text-gray-600 mt-0.5">
                      {step.description}
                    </span>
                  </span>
                </div>
              </>
            ) : (
              <>
                {stepIdx !== updatedSteps.length - 1 ? (
                  <div
                    aria-hidden="true"
                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                  />
                ) : null}
                <div className="group relative flex items-start">
                  <span aria-hidden="true" className="flex h-9 items-center">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white group-hover:border-gray-400 transition-colors">
                      <span className="h-2.5 w-2.5 rounded-full bg-transparent group-hover:bg-gray-300 transition-colors" />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      {step.name}
                    </span>
                    <span className="text-xs text-gray-500 mt-0.5">
                      {step.description}
                    </span>
                  </span>
                </div>
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
